import type {
  InfiniteQueryHook,
  InfiniteQueryHookOptions,
} from "react-query-kit";
import type { ReactNode } from "react";

import { useGetAllInfiniteHFs } from "../getAllHFs";
import { useGetAllInfiniteDoctors } from "../getAllDoctors";
import { useGetAllInfiniteStaffs } from "../getAllStaffs";
import type {
  IInfiniteDoctorsResponse,
  IInfiniteHfResponse,
  IInfinitePatientsResponse,
  IInfinitePatternResponse,
  IInfiniteStaffResponse,
  IInfiniteTerminologiesResponse,
  IInfiniteVisitHistoriesResponse,
} from "../types";
import { useGetAllInfinitePatterns } from "../getAllPattern";
import { useGetAllInfiniteTerminologies } from "../getAllTerminologies";
import { useGetAllInfiniteAssignableDoctorsToMsp } from "../getAllAssignableDoctorsToMsp";

import type { IMetaOption, IOption } from "@/interfaces";
import type {
  IExtractParamsInfiniteQueryHook,
  IExtractResponseInfiniteQueryHook,
  IOmitSafety,
} from "@/utils/typeTools";
import { useGetAllInfiniteVisitHistories } from "@api/infiniteDropdowns/getAllVisitHistories";
import { useGetAllSuggestedPatterns } from "@api/infiniteDropdowns/getAllSuggestedPattern";
import { useGetAllInfiniteAssignableDoctorsToStaff } from "@api/infiniteDropdowns/getAllAssignableDoctorsToStaff";
import { useGetAllInfinitePatients } from "@api/infiniteDropdowns/getAllPatients";
import { useGetAllInfiniteDiseases } from "@api/infiniteDropdowns/getAllDisease";
import type { IDiseasesEndpoint } from "@api/infinite";
import { useGetAllInfiniteMsp } from "@api/infiniteDropdowns/getAllMsp";

export type IInfiniteDropdownType =
  | "doctor"
  | "patient"
  | "staff"
  | "hf"
  | "patterns"
  | "suggestedPatterns"
  | "terminologies"
  | "assignableDoctorsToStaff"
  | "assignableDoctorsToMsp"
  | "visitHistories"
  | "diseases"
  | "mspName";

type IInfiniteQueryHooks = {
  [key in IInfiniteDropdownType]: InfiniteQueryHook<
    IInfiniteResponse[key],
    IInfiniteParams[key],
    Error,
    number
  >;
};

export const infiniteQueryHooksMap: IInfiniteQueryHooks = {
  hf: useGetAllInfiniteHFs,
  doctor: useGetAllInfiniteDoctors,
  staff: useGetAllInfiniteStaffs,
  patterns: useGetAllInfinitePatterns,
  suggestedPatterns: useGetAllSuggestedPatterns,
  assignableDoctorsToStaff: useGetAllInfiniteAssignableDoctorsToStaff,
  assignableDoctorsToMsp: useGetAllInfiniteAssignableDoctorsToMsp,
  terminologies: useGetAllInfiniteTerminologies,
  visitHistories: useGetAllInfiniteVisitHistories,
  patient: useGetAllInfinitePatients,
  diseases: useGetAllInfiniteDiseases,
  mspName: useGetAllInfiniteMsp,
};

interface ITypeOf {
  hf: typeof useGetAllInfiniteHFs;
  doctor: typeof useGetAllInfiniteDoctors;
  staff: typeof useGetAllInfiniteStaffs;
  patterns: typeof useGetAllInfinitePatterns;
  suggestedPatterns: typeof useGetAllSuggestedPatterns;
  assignableDoctorsToStaff: typeof useGetAllInfiniteAssignableDoctorsToStaff;
  assignableDoctorsToMsp: typeof useGetAllInfiniteAssignableDoctorsToMsp;
  terminologies: typeof useGetAllInfiniteTerminologies;
  visitHistories: typeof useGetAllInfiniteVisitHistories;
  patient: typeof useGetAllInfinitePatients;
  diseases: typeof useGetAllInfiniteDiseases;
  mspName: typeof useGetAllInfiniteMsp;
}

// Mapping object for response types
interface IResponseMap {
  hf: IExtractResponseInfiniteQueryHook<ITypeOf["hf"]>;
  doctor: IExtractResponseInfiniteQueryHook<ITypeOf["doctor"]>;
  staff: IExtractResponseInfiniteQueryHook<ITypeOf["staff"]>;
  patterns: IExtractResponseInfiniteQueryHook<ITypeOf["patterns"]>;
  suggestedPatterns: IExtractResponseInfiniteQueryHook<
    ITypeOf["suggestedPatterns"]
  >;
  assignableDoctorsToStaff: IExtractResponseInfiniteQueryHook<
    ITypeOf["assignableDoctorsToStaff"]
  >;
  assignableDoctorsToMsp: IExtractResponseInfiniteQueryHook<
    ITypeOf["assignableDoctorsToMsp"]
  >;
  terminologies: IExtractResponseInfiniteQueryHook<ITypeOf["terminologies"]>;
  visitHistories: IExtractResponseInfiniteQueryHook<ITypeOf["visitHistories"]>;
  patient: IExtractResponseInfiniteQueryHook<ITypeOf["patient"]>;
  diseases: IExtractResponseInfiniteQueryHook<ITypeOf["diseases"]>;
  mspName: IExtractResponseInfiniteQueryHook<ITypeOf["mspName"]>;
}

interface IListResponseMap {
  hf: IInfiniteHfResponse;
  doctor: IInfiniteDoctorsResponse;
  staff: IInfiniteStaffResponse;
  patterns: IInfinitePatternResponse;
  suggestedPatterns: IInfinitePatternResponse;
  assignableDoctorsToStaff: IInfiniteDoctorsResponse;
  assignableDoctorsToMsp: IInfiniteDoctorsResponse;
  terminologies: IInfiniteTerminologiesResponse;
  visitHistories: IInfiniteVisitHistoriesResponse;
  patient: IInfinitePatientsResponse;
  diseases: IDiseasesEndpoint;
  mspName: IOption;
}

// Mapping object for parameter types
interface IParamsMap {
  hf: IExtractParamsInfiniteQueryHook<ITypeOf["hf"]>;
  doctor: IExtractParamsInfiniteQueryHook<ITypeOf["doctor"]>;
  staff: IExtractParamsInfiniteQueryHook<ITypeOf["staff"]>;
  patterns: IExtractParamsInfiniteQueryHook<ITypeOf["patterns"]>;
  suggestedPatterns: IExtractParamsInfiniteQueryHook<
    ITypeOf["suggestedPatterns"]
  >;
  assignableDoctorsToStaff: IExtractParamsInfiniteQueryHook<
    ITypeOf["assignableDoctorsToStaff"]
  >;
  assignableDoctorsToMsp: IExtractParamsInfiniteQueryHook<
    ITypeOf["assignableDoctorsToMsp"]
  >;
  terminologies: IExtractParamsInfiniteQueryHook<ITypeOf["terminologies"]>;
  visitHistories: IExtractParamsInfiniteQueryHook<ITypeOf["visitHistories"]>;
  patient: IExtractParamsInfiniteQueryHook<ITypeOf["patient"]>;
  diseases: IExtractParamsInfiniteQueryHook<ITypeOf["diseases"]>;
  mspName: IExtractParamsInfiniteQueryHook<ITypeOf["mspName"]>;
}

// Use mapped types to create IResponse and IParams
export type IInfiniteResponse = {
  [key in IInfiniteDropdownType]: IResponseMap[key];
};
export type IInfiniteListResponse = {
  [key in IInfiniteDropdownType]: IListResponseMap[key];
};

export type IInfiniteParams = {
  [key in IInfiniteDropdownType]: IParamsMap[key];
};

export type IAllInfiniteConfig = {
  [key in IInfiniteDropdownType]: IInfiniteConfig<key>;
};

export interface IInfiniteConfig<T extends IInfiniteDropdownType> {
  useHook: InfiniteQueryHook<
    IInfiniteResponse[T],
    IInfiniteParams[T],
    Error,
    number
  >;
  /**
   * you can define options for your hook like control enabling of the query
   * @param variables
   * @returns queryHook options except variables
   */
  hookOptions?: (
    variables: IInfiniteParams[T],
  ) => IOmitSafety<
    InfiniteQueryHookOptions<
      IInfiniteResponse[T],
      Error,
      IInfiniteResponse[T],
      IInfiniteParams[T],
      number
    >,
    "variables" | "select"
  >;
  mapper: (
    value: IInfiniteResponse[T],
  ) => IMetaOption<IInfiniteListResponse[T]>[];
  itemNode?: (value: IMetaOption<IInfiniteListResponse[T]>) => ReactNode;
}
